import { defineStore } from 'pinia';
import type { State } from './types';
import * as rmAgent from './agent.ts';
import * as rmAuth from './auth.ts';
import * as rmRacks from './racks.ts';
import * as rmRooms from './rooms.ts';
import * as rmBilling from './billing.ts';
import * as rmBranding from './branding.ts';
import * as rmWebhooks from './webhooks.ts';
import * as rmApiKeys from './apikeys.ts';
import * as rmDomains from './domains.ts';
import * as rmPreferences from './preferences.ts';
import * as rmContact from './contact.ts';
import * as rmTeams from './teams.ts';
import * as rmDevelopment from './development.ts';
import * as rmAdmin from './admin.ts';
import * as rmMfa from './auth/index.ts';
import * as rmSlots from './slots.ts';
import * as rmSnapshots from './snapshots.ts';
import { useWorkspaceSwitcherPopupStore } from '../stores/workspaceSwitcherPopup';

const authorizedDomainsMatch = () => {
  if (import.meta.env.VITE_SENTRY_ENV == 'production') {
    return `+(${import.meta.env.VITE_HOSTNAME}|rackmanage.net)`;
  } else {
    return `+(localhost|${import.meta.env.VITE_HOSTNAME}|rackmanage.net|*.${import.meta.env.VITE_DEV_DOMAIN})`;
  }
};

export const useStore = defineStore('rackmanage-db', {
  // convert to a function
  state: (): State => ({
    appLoading: true,
    user: null,
    curDomain: null,
    authorizedDomains: authorizedDomainsMatch(),
    checkedDomain: null,
    colors: {
      primary: {
        red: 16,
        green: 185,
        blue: 129,
      },
      loginBackground: {
        red: 30,
        green: 41,
        blue: 59,
      },
      teamId: null,
    },
    searchKey: null,
    purchaseFlow: 'none',
    team: null,
    teamData: null,
    userMode: 'user',
    firebaseApp: '[DEFAULT]',
    dbid: import.meta.env.VITE_FIREBASE_DBID,
    userPreferences: {
      darkMode: false,
      displayMode: 'table',
      numbering: false,
      numberingDirection: 'DESC',
      nameOverlay: 2,
      autosave: null,
      unitSystem: 'imperial',
      tableSorting: {},
    },
    system: {
      permissions: [],
    },
    build: import.meta.env.VITE_BUILD,
    ssoLogoutState: null as string | null,
    userTeams: [],
    lastUsedAuthMethod: null,
  }),
  actions: {
    resetColorPreferences() {
      this.colors = {
        primary: {
          red: 16,
          green: 185,
          blue: 129,
        },
        loginBackground: {
          red: 30,
          green: 41,
          blue: 59,
        },
      };
      document.querySelector<HTMLElement>(':root')?.style.setProperty('--primary-color', '16 185 129');
      document.querySelector<HTMLElement>(':root')?.style.setProperty('--login-background-color', '30 41 59');
    },
    ...rmAgent,
    ...rmAuth,
    ...rmMfa,
    ...rmRacks,
    ...rmRooms,
    ...rmBilling,
    ...rmBranding,
    ...rmWebhooks,
    ...rmApiKeys,
    ...rmDomains,
    ...rmPreferences,
    ...rmContact,
    ...rmTeams,
    ...rmDevelopment,
    ...rmAdmin,
    ...rmSlots,
    ...rmSnapshots,
    async switchWorkspaceWithPopup(toOption: { value: string; label: string; color: string | null }) {
      const fromOption = this.getCurrentWorkspaceOption();
      if (fromOption.value === toOption.value) return;
      const popupStore = useWorkspaceSwitcherPopupStore();
      const minDisplayMs = 1000;
      const startedAt = Date.now();
      popupStore.open(fromOption, toOption);
      try {
        if (toOption.value === 'user') await this.switchToPersonalWorkspace();
        else await this.switchTeam(toOption.value);
      } finally {
        const elapsed = Date.now() - startedAt;
        if (elapsed < minDisplayMs) {
          await new Promise(r => setTimeout(r, minDisplayMs - elapsed));
        }
        popupStore.close();
      }
    },
  },
  persist: true,
});
